// Omnix/client/ui/AudioRecorder.js
import { ComponentBase } from "../../core/ComponentBase.js";

export class AudioRecorder extends ComponentBase {
    static styles = `
        .audio-recorder-modal .modal-dialog { max-width: 400px; }
        .audio-recorder-modal .modal-body { text-align: center; }
        .recorder-canvas { width: 100%; height: 80px; background-color: var(--bs-tertiary-bg); border-radius: var(--bs-border-radius); }
        .recorder-timer { font-family: monospace; font-size: 1.5rem; margin: 1rem 0; }
        .recorder-controls .btn { width: 60px; height: 60px; border-radius: 50%; font-size: 1.5rem; }
        .recorder-preview { width: 100%; margin-top: 1rem; }
    `;

    constructor(config) {
        super(config);
        this.stream = null;
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.finalAudioFile = null;
        this.isRecording = false;
        this.timerInterval = null;
        this.recordingStartTime = 0;
        this.animationFrameId = null;
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade audio-recorder-modal';
        this.container.id = `${this.config.instanceId}-modal`;
        this.container.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header"><h5 class="modal-title">${this.t('Record Audio')}</h5><button type="button" class="btn-close" data-bs-dismiss="modal"></button></div>
                    <div class="modal-body">
                        <canvas class="recorder-canvas"></canvas>
                        <div class="recorder-timer">00:00</div>
                        <div class="recorder-controls my-3">
                            <button class="btn btn-danger" id="record-btn"><i class="bi bi-mic-fill"></i></button>
                        </div>
                        <audio class="recorder-preview" controls></audio>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${this.t('Cancel')}</button>
                        <button type="button" class="btn btn-primary" id="save-btn" disabled>${this.t('Use Recording')}</button>
                    </div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);
        this.canvas = this.container.querySelector('.recorder-canvas');
        this.canvasCtx = this.canvas.getContext('2d');
        this.recordBtn = this.container.querySelector('#record-btn');
        this.timerEl = this.container.querySelector('.recorder-timer');
        this.previewEl = this.container.querySelector('.recorder-preview');
        this.saveBtn = this.container.querySelector('#save-btn');

        this.recordBtn.addEventListener('click', () => this.isRecording ? this.stopRecording() : this.startRecording());
        this.saveBtn.addEventListener('click', this.handleSave.bind(this));
        this.container.addEventListener('hidden.bs.modal', this.cleanup.bind(this));
    }

    async show() {
        this.reset();
        this.modalInstance.show();
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.visualize();
        } catch (err) {
            this.appClient.notifier.error("Microphone access was denied.");
            this.hide();
        }
    }
    
    hide() { this.modalInstance.hide(); }

    reset() {
        this.audioChunks = [];
        this.finalAudioFile = null;
        this.isRecording = false;
        this.recordBtn.innerHTML = '<i class="bi bi-mic-fill"></i>';
        this.recordBtn.classList.replace('btn-secondary', 'btn-danger');
        this.previewEl.src = '';
        this.previewEl.style.display = 'none';
        this.saveBtn.disabled = true;
        this.timerEl.textContent = '00:00';
    }

    startRecording() {
        this.reset();
        this.isRecording = true;
        this.mediaRecorder = new MediaRecorder(this.stream);
        this.mediaRecorder.ondataavailable = e => this.audioChunks.push(e.data);
        this.mediaRecorder.onstop = () => {
            const audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
            const fileName = `recording-${new Date().toISOString()}.wav`;
            this.finalAudioFile = new File([audioBlob], fileName, { type: 'audio/wav' });
            this.previewEl.src = URL.createObjectURL(audioBlob);
            this.previewEl.style.display = 'block';
            this.saveBtn.disabled = false;
        };
        this.mediaRecorder.start();
        this.recordBtn.innerHTML = '<i class="bi bi-stop-fill"></i>';
        this.recordBtn.classList.replace('btn-danger', 'btn-secondary');
        this.startTimer();
    }

    stopRecording() {
        if (this.mediaRecorder) {
            this.mediaRecorder.stop();
        }
        this.isRecording = false;
        this.stopTimer();
    }

    startTimer() {
        this.recordingStartTime = Date.now();
        this.timerInterval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000);
            const minutes = String(Math.floor(elapsed / 60)).padStart(2, '0');
            const seconds = String(elapsed % 60).padStart(2, '0');
            this.timerEl.textContent = `${minutes}:${seconds}`;
        }, 1000);
    }

    stopTimer() { clearInterval(this.timerInterval); }
    
    handleSave() {
        if (this.finalAudioFile) {
            this.emit('recordingSaved', { file: this.finalAudioFile });
            this.hide();
        }
    }

    visualize() {
        if (!this.stream) return;
        this.audioContext = new AudioContext();
        const source = this.audioContext.createMediaStreamSource(this.stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;
        const bufferLength = this.analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        source.connect(this.analyser);

        const draw = () => {
            if (!this.isRecording) {
                this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
                return;
            };
            this.animationFrameId = requestAnimationFrame(draw);
            this.analyser.getByteTimeDomainData(dataArray);
            this.canvasCtx.fillStyle = 'var(--bs-body-bg)';
            this.canvasCtx.fillRect(0, 0, this.canvas.width, this.canvas.height);
            this.canvasCtx.lineWidth = 2;
            this.canvasCtx.strokeStyle = 'var(--bs-primary)';
            this.canvasCtx.beginPath();
            const sliceWidth = this.canvas.width * 1.0 / bufferLength;
            let x = 0;
            for (let i = 0; i < bufferLength; i++) {
                const v = dataArray[i] / 128.0;
                const y = v * this.canvas.height / 2;
                if (i === 0) this.canvasCtx.moveTo(x, y);
                else this.canvasCtx.lineTo(x, y);
                x += sliceWidth;
            }
            this.canvasCtx.lineTo(this.canvas.width, this.canvas.height / 2);
            this.canvasCtx.stroke();
        };
        draw();
    }
    
    cleanup() {
        this.stopRecording();
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        cancelAnimationFrame(this.animationFrameId);
    }

    render() { return this.container; }
    destroy() { this.cleanup(); this.modalInstance?.dispose(); this.container?.remove(); super.destroy(); }
}